import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/TechStack.css";
import { RiFileExcel2Fill } from "react-icons/ri";
import { VscVscode } from "react-icons/vsc";
import { 
  SiMysql, SiPostgresql, SiTableau, SiPython, 
  SiPandas, SiNumpy, SiJupyter,
  SiR, SiGit, SiFigma,
  SiGoogleanalytics, SiPosthog
} from "react-icons/si";

gsap.registerPlugin(ScrollTrigger);

// Custom PowerBI Icon Component - Three vertical bars in yellow/orange gradient
const PowerBIIcon = ({ style }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="4" y="12" width="4" height="8" rx="2" fill="#F2C811"/>
    <rect x="10" y="8" width="4" height="12" rx="2" fill="#FFB900"/>
    <rect x="16" y="4" width="4" height="16" rx="2" fill="#FF8C00"/>
  </svg>
);

// Custom DuckDB Icon Component - Black circle with yellow circle and pill shape
const DuckDBIcon = ({ style }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    style={style}
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="11" fill="#000000"/>
    <circle cx="9" cy="12" r="4" fill="#FFF000"/>
    <rect x="11" y="10" width="6" height="4" rx="2" fill="#FFF000"/>
  </svg>
);

const techStackData = [
  { name: "MySQL", icon: <SiMysql />, color: "#00758F" },
  { name: "PostgreSQL", icon: <SiPostgresql />, color: "#336791" },
  { name: "Excel", icon: <RiFileExcel2Fill />, color: "#217346" },
  { name: "Tableau", icon: <SiTableau />, color: "#E97627" },
  { name: "Python", icon: <SiPython />, color: "#3776AB" },
  { name: "Pandas", icon: <SiPandas />, color: "#150458" },
  { name: "NumPy", icon: <SiNumpy />, color: "#013243" },
  { name: "R", icon: <SiR />, color: "#276DC3" },
  { name: "VS Code", icon: <VscVscode />, color: "#007ACC" },
  { name: "Git", icon: <SiGit />, color: "#F05032" },
  { name: "Jupyter", icon: <SiJupyter />, color: "#F37626" },
  { name: "Figma", icon: <SiFigma />, color: "#F24E1E" },
  { name: "Google Analytics", icon: <SiGoogleanalytics />, color: "#E37400" },
  { name: "PostHog", icon: <SiPosthog />, color: "#FF6D5A" },
  { name: "Power BI", icon: <PowerBIIcon />, color: "#F2C811" },
  { name: "DuckDB", icon: <DuckDBIcon />, color: "#FFF000" },
];

export default function TechStack() {
  const techStackRef = useRef(null);
  
  useEffect(() => {
    console.log("TechStack component mounted");
    
    const totalItems = techStackData.length;
    const cols = 2; 
    const rows = Math.ceil(totalItems / cols);
    const grid = [rows, cols];
    
    console.log("TechStack grid dimensions:", grid);
    
    gsap.set(".tech-icon-container", { opacity: 1, scale: 1 });
    
    gsap.fromTo(".tech-title",
      {
        y: 30,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#tech-stack",
          start: "top 90%",
          toggleActions: "play none none reverse"
        }
      }
    );

    gsap.fromTo(".tech-icon-container",
      {
        scale: 0.1,
        y: -250,
        opacity: 0
      },
      {
        y: 0,
        scale: 1,
        duration: 0.5,
        opacity: 1,
        stagger: {
        grid: [7,15],
        from: "center",
        amount: 1 ,
      },
        ease: "power1.inOut", 
        scrollTrigger: {
          trigger: "#tech-stack",
          start: "top 70%",
          toggleActions: "play none none reverse" 
        }
      }
    );
    
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const handleIconHover = (e, enter) => {
    const target = e.currentTarget;
    
    if (enter) {
      gsap.to(target, {
        y: -5,
        scale: 1.1,
        duration: 0.3,
        ease: "power2.out"
      });
      
      gsap.to(target.querySelector('.tech-icon'), {
        color: "var(--green-bright)",
        duration: 0.3
      });
    } else {
      gsap.to(target, {
        y: 0,
        scale: 1,
        duration: 0.3,
        ease: "power2.out"
      });
      
      gsap.to(target.querySelector('.tech-icon'), {
        color: target.dataset.color,
        duration: 0.3
      });
    }
  };

  return (
    <section id="tech-stack" ref={techStackRef}>
      <div className="section-header">
        <span className="section-title tech-title">Tech Stack</span>
      </div>
      
      <div className="tech-grid-container">
        {techStackData.map((tool, index) => (
          <div
            key={tool.name}
            className="tech-icon-container"
            data-color={tool.color}
            title={tool.name} 
            onMouseEnter={(e) => handleIconHover(e, true)}
            onMouseLeave={(e) => handleIconHover(e, false)}
          >
            <div className="tech-icon" style={{ color: tool.color }}>
              {tool.icon}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}